import {
  type AddProToJobModalProps,
  type BulkMessagingModalProps,
  type CopilotScoreFeedbackModalProps,
  type UploadResumesModalProps,
  useDialogStore,
} from './dialog.store';
import type {
  MarketingReportType,
  ReportCandidate,
} from '@/components/LaborMarket/labor-market-report-options';
import type {
  GoldUpgradeConfirmedModalProps,
  UpgradeToGoldModalProps,
  UpgradeToGoldVariant,
} from './dialog.types';

export class DialogService {
  private store = useDialogStore();

  public get isCreateProjectDialogOpen(): boolean {
    return this.store.isCreateProjectDialogOpen;
  }

  public set isCreateProjectDialogOpen(value: boolean) {
    this.store.isCreateProjectDialogOpen = value;
  }

  public get projectDescriptionOverlayProps(): {
    isOpen: boolean;
    projectId?: number;
    jobId?: number;
  } {
    return this.store.projectDescriptionOverlayProps;
  }

  public set projectDescriptionOverlayProps(value: {
    isOpen: boolean;
    projectId?: number;
    jobId?: number;
  }) {
    this.store.projectDescriptionOverlayProps = value;
  }

  public closeProjectDescriptionOverlay(): void {
    this.store.projectDescriptionOverlayProps.isOpen = false;
  }

  public get copilotScoreFeedbackModalProps(): CopilotScoreFeedbackModalProps {
    return this.store.copilotScoreFeedbackModalProps;
  }

  public set copilotScoreFeedbackModalProps(value: CopilotScoreFeedbackModalProps) {
    this.store.copilotScoreFeedbackModalProps = value;
  }

  public get addProToJobModalProps(): AddProToJobModalProps {
    return this.store.addProToJobModalProps;
  }

  public set addProToJobModalProps(value: AddProToJobModalProps) {
    this.store.addProToJobModalProps = value;
  }

  public get isLaborMarketReportsModalOpen(): boolean {
    return this.store.isLaborMarketReportsModalOpen;
  }

  public set isLaborMarketReportsModalOpen(value: boolean) {
    this.store.isLaborMarketReportsModalOpen = value;
  }

  public get laborMarketReportContext(): {
    candidates: ReportCandidate[];
    defaultType: MarketingReportType;
  } | null {
    return this.store.laborMarketReportContext;
  }

  public set laborMarketReportContext(
    value: { candidates: ReportCandidate[]; defaultType: MarketingReportType } | null,
  ) {
    this.store.laborMarketReportContext = value;
  }

  public openLaborMarketReports(options?: {
    candidates?: ReportCandidate[];
    defaultType?: MarketingReportType;
  }): void {
    this.store.laborMarketReportContext =
      options && (options.candidates?.length || options.defaultType)
        ? {
            candidates: options.candidates ?? [],
            defaultType: options.defaultType ?? 'JOB_MATCH',
          }
        : null;
    this.store.isLaborMarketReportsModalOpen = true;
  }

  public get applicantFlowInsightsSuccessModalProps():
    | { isOpen: false }
    | { isOpen: true; referenceId: string } {
    return this.store.applicantFlowInsightsSuccessModalProps;
  }

  public set applicantFlowInsightsSuccessModalProps(
    value: { isOpen: false } | { isOpen: true; referenceId: string },
  ) {
    this.store.applicantFlowInsightsSuccessModalProps = value;
  }

  public get jobMatchReportSuccessModalProps():
    | { isOpen: false }
    | { isOpen: true; referenceId: string } {
    return this.store.jobMatchReportSuccessModalProps;
  }

  public set jobMatchReportSuccessModalProps(
    value: { isOpen: false } | { isOpen: true; referenceId: string },
  ) {
    this.store.jobMatchReportSuccessModalProps = value;
  }

  public get bulkMessagingModalProps(): BulkMessagingModalProps {
    return this.store.bulkMessagingModalProps;
  }

  public set bulkMessagingModalProps(value: BulkMessagingModalProps) {
    this.store.bulkMessagingModalProps = value;
  }

  public get upgradeToGoldModalProps(): UpgradeToGoldModalProps {
    return this.store.upgradeToGoldModalProps;
  }

  public set upgradeToGoldModalProps(value: UpgradeToGoldModalProps) {
    this.store.upgradeToGoldModalProps = value;
  }

  public get goldUpgradeConfirmedModalProps(): GoldUpgradeConfirmedModalProps {
    return this.store.goldUpgradeConfirmedModalProps;
  }

  public set goldUpgradeConfirmedModalProps(value: GoldUpgradeConfirmedModalProps) {
    this.store.goldUpgradeConfirmedModalProps = value;
  }

  public get uploadResumesModalProps(): UploadResumesModalProps {
    return this.store.uploadResumesModalProps;
  }

  public set uploadResumesModalProps(value: UploadResumesModalProps) {
    this.store.uploadResumesModalProps = value;
  }

  public openUpgradeToGoldModal(variant: UpgradeToGoldVariant): void {
    const requestedGold = this.upgradeToGoldModalProps?.hasRequestedGold;
    if (requestedGold) {
      this.goldUpgradeConfirmedModalProps = {
        isOpen: true,
      };
    } else {
      this.upgradeToGoldModalProps = {
        isOpen: true,
        variant,
        hasRequestedGold: false,
      };
    }
  }
}
